<template>
  <div class="motor-principle-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$τ = N · B · I · A sinθ$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <!-- Permanent Magnets -->
          <rect x="20" y="30" width="70" height="160" fill="rgba(239, 68, 68, 0.15)" stroke="#ef4444" stroke-width="2" rx="8" />
          <text x="45" y="115" fill="#ef4444" font-weight="900" font-size="20">N</text>
          
          <rect x="310" y="30" width="70" height="160" fill="rgba(16, 185, 129, 0.15)" stroke="#10b981" stroke-width="2" rx="8" />
          <text x="335" y="115" fill="#10b981" font-weight="900" font-size="20">S</text>

          <!-- Magnetic Field Lines (B) -->
          <g>
            <line v-for="i in 5" :key="'b'+i" x1="90" :y1="50 + i*20" x2="310" :y2="50 + i*20" stroke="rgba(6, 182, 212, 0.3)" stroke-width="1" marker-end="url(#b-arrow)" />
          </g>

          <!-- Rotating Coil (Armature) -->
          <g :transform="`translate(200, 110) scale(${Math.cos(angleRad)}, 1)`">
             <!-- 线圈主体 -->
             <rect x="-45" y="-60" width="90" height="120" fill="none" stroke="#f59e0b" stroke-width="4" rx="2" />
             <!-- 换向器细节 -->
             <circle cx="0" cy="0" r="12" fill="#1e293b" stroke="#475569" stroke-width="2" />
             <line x1="-12" y1="0" x2="12" y2="0" stroke="#94a3b8" stroke-width="1.5" />
          </g>

          <!-- Force Vectors (F) - 通电时显示 -->
          <g v-if="powerOn">
             <line :x1="200 + 45*Math.cos(angleRad)" y1="110" :x2="200 + 45*Math.cos(angleRad)" :y2="110 - forceLen" stroke="#0ea5e9" stroke-width="3" marker-end="url(#f-arrow)" />
             <line :x1="200 - 45*Math.cos(angleRad)" y1="110" :x2="200 - 45*Math.cos(angleRad)" :y2="110 + forceLen" stroke="#0ea5e9" stroke-width="3" marker-end="url(#f-arrow)" />
             <text :x="200 + 45*Math.cos(angleRad) + 10" :y="110 - forceLen - 5" fill="#0ea5e9" font-size="10" font-weight="700">安培力 F</text>
          </g>

          <defs>
            <marker id="b-arrow" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto"><polygon points="0 0, 6 2, 0 4" fill="rgba(6, 182, 212, 0.6)" /></marker>
            <marker id="f-arrow" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto"><polygon points="0 0, 6 2, 0 4" fill="#0ea5e9" /></marker>
          </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>换向器状态</label>
              <span class="val">{{ Math.abs(Math.cos(angleRad)) < 0.1 ? '正在换向' : '持续转动' }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="s-item">
                 <label>电流强度 (I): {{ current }} A</label>
                 <el-slider v-model="current" :min="1" :max="15" size="small" :disabled="!powerOn"></el-slider>
              </div>
              <div class="action-row">
                 <el-button :type="powerOn ? 'danger' : 'primary'" size="small" @click="powerOn = !powerOn" :icon="powerOn ? 'el-icon-video-pause' : 'el-icon-video-play'">
                    {{ powerOn ? '切断电流' : '接通电源' }}
                 </el-button>
                 <el-button @click="angle = 0" size="small" plain icon="el-icon-refresh">重置相位</el-button>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心机理：</strong>电动机将<strong>电能转化为机械能</strong>。通过<strong>换向器</strong>在平衡位置不断改变线圈中的电流方向，从而保证电磁力矩方向保持一致，维持旋转。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MotorPrincipleViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      angle: 0,
      current: 8,
      powerOn: true,
      timer: null
    }
  },
  computed: {
    angleRad() { return (this.angle * Math.PI) / 180; },
    forceLen() { return this.current * 8; }
  },
  methods: {
    update() {
      if (this.powerOn) {
         this.angle = (this.angle + (this.current * 0.4)) % 360;
      }
      this.timer = requestAnimationFrame(this.update);
    }
  },
  mounted() { this.update(); },
  beforeDestroy() { cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.motor-principle-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #0ea5e9; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; }
.s-item { label { font-size: 12px; color: #64748b; font-weight: 700; display: block; margin-bottom: 2px; } }
.action-row { display: flex; gap: 8px; border-top: 1px solid #f1f5f9; padding-top: 10px; .el-button { flex: 1; font-weight: 700; } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
